<script setup lang="ts">
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import { formatMoney } from '@/domain/store-setup/money';
  import type { ILiveOther } from '@/interfaces/IMenuImport';

  /**
   * One dish of the others card (plan §3.5): its name and price, and
   * «Ocultar» or, once marked, its name struck with «Se oculta» and «No
   * ocultar». The button waits for the server while `busy`.
   */
  const props = defineProps<{
    other: ILiveOther;
    /** Marked to be hidden when publishing. */
    hiding: boolean;
    busy: boolean;
    /** Id of the name, for the button's `aria-describedby`. */
    nameId: string;
    currency: string;
    country: string;
  }>();
  const emit = defineEmits<{ (e: 'toggle'): void }>();

  function money(value: number): string {
    return formatMoney(value, props.currency, props.country);
  }
</script>

<template>
  <li class="v2-menu-others-row" :class="{ 'is-hiding': hiding }">
    <span class="row-text">
      <span :id="nameId" class="row-name">{{ other.name }}</span>
      <PosUiBadge v-if="hiding" size="sm">{{
        $t('menuImport.update.willHide')
      }}</PosUiBadge>
    </span>
    <span class="row-end">
      <span class="row-price">{{ money(other.price) }}</span>
      <PosUiButton
        color="neutral"
        variant="outline"
        size="sm"
        class="row-action"
        :loading="busy"
        :aria-describedby="nameId"
        @click="emit('toggle')"
        >{{
          hiding ? $t('menuImport.update.unhide') : $t('menuImport.update.hide')
        }}</PosUiButton
      >
    </span>
  </li>
</template>

<style scoped lang="scss">
  .v2-menu-others-row {
    @apply tw-flex tw-min-h-12 tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-border-0 tw-border-b tw-border-solid tw-border-neutral-100 tw-py-1.5;
    &:last-child {
      @apply tw-border-b-0;
    }

    // The name keeps room of its own: price and button wrap under it first.
    .row-text {
      @apply tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1;
      flex: 1 1 10rem;
    }
    .row-name {
      @apply tw-min-w-0 tw-break-words tw-text-sm tw-text-neutral-900;
    }
    .row-end {
      @apply tw-ml-auto tw-flex tw-shrink-0 tw-items-center tw-gap-3;
    }
    .row-price {
      @apply tw-whitespace-nowrap tw-text-sm tw-tabular-nums tw-text-neutral-600;
    }
    .row-action {
      @apply tw-min-h-11 md:tw-min-h-0;
    }
    &.is-hiding .row-name {
      @apply tw-text-neutral-500 tw-line-through;
    }
  }
</style>
